/* ============================================================================
   TAP TO ZOOM, SWIPE TO BROWSE. 3 October 2026.

   Sharon, on her phone: "If I click on the large photo, it should just pop up a
   zoom screen... and when it's in a zoom view, I should be able to slide left,
   right to go to the next image... in order for me to slide to the next
   different images I have to click on that two kind of like dot almost like an
   arrow and that's not intuitive."

   Until this, the buy box gallery answered to exactly three things: the ‹ › 
   arrows, the dots, and a thumbnail click. There was no touch handling of any
   kind on the photograph and no zoom anywhere -- measured, not assumed: the
   section carries no touchstart, pointerdown or lightbox code.

   ============================================================================
   IT DRIVES THE EXISTING CONTROLS RATHER THAN KEEPING ITS OWN INDEX

   The gallery's index lives in a closure inside rn-buybox.liquid, alongside the
   combined-list maths that walks every colourway, picks the right swatch and
   rebuilds the dot row. Reimplementing any of that here would give the page two
   opinions about which photograph is showing, and sooner or later they would
   disagree -- which is exactly the bug class that produced "the buy box posted
   the first variant".

   So a swipe CLICKS [data-bb-next] or [data-bb-prev]. One source of truth. If
   the section ever changes how it advances, this follows for free.

   ============================================================================
   WHAT IT DELIBERATELY DOES NOT DO

   - It does not touch the thumbnail rail's contents. rn-pdp-stack.js's first
     rule is that nothing may add, remove or reorder a .bb-th, because the buy
     box resolves a click by the element's position among its siblings.
   - It does not open while the 360° viewer is up; that overlay covers .bb-main
     and a tap there belongs to it.
   - It ignores taps that land on a button, link or the badge -- those have
     their own jobs.

   ============================================================================
   VIDEOS OPEN TOO, 9 October 2026, Stevie: tapping a gallery video while it
   played opened the zoom onto a blank dark screen. The overlay only ever knew
   how to show an <img>, and when the main frame holds a <video> there is no
   photograph to copy -- so it showed nothing. It now shows the video itself
   (muted, looping, with the browser's own controls), picking up from the
   frame the shopper was watching, and stops it again on close so nothing keeps
   downloading in the background. Photographs behave exactly as before.
   ============================================================================ */
(function () {
  'use strict';

  /* EVERY GALLERY ON THE SITE, NAMED ONCE. 3 Oct 2026.

     The PDP buy box and the homepage Reya block are the same idea with
     different class names, and the Add-Ons page has no gallery of its own --
     its cards link to product pages, which are buy boxes. Listing them here
     rather than hardcoding one set means a third gallery costs one line.

     `block` is a selector that, when present and visible, means something else
     owns the tap -- the 360 viewer covers the PDP photograph exactly. */
  var GALLERIES = [
    { root: '[id^="rn-bb-"]',      main: '.bb-main',  prev: '[data-bb-prev]',
      next: '[data-bb-next]',      dots: '.bb-dot',   block: '.bb-ar-overlay',
      skip: '.bb-ctrl, .bb-badge' },
    { root: '[id^="rn-hp-reya-"]', main: '.rc-media', prev: '[data-gallery-prev]',
      next: '[data-gallery-next]', dots: '.rc-dot',   block: null,
      skip: '.rc-ctrl' }
  ];

  /* A drag shorter than this is a tap, not a swipe. 30px is about a thumb's
     width of movement -- low enough to feel responsive, high enough that the
     small wobble in a tap does not register as a flick. */
  var SWIPE_MIN = 30;
  /* If the finger moved further up/down than sideways, the shopper is scrolling
     the page, not browsing photographs. Never steal that. */
  var SWIPE_RATIO = 1.2;

  function injectStyles() {
    if (document.getElementById('rn-gallery-touch-css')) return;
    var s = document.createElement('style');
    s.id = 'rn-gallery-touch-css';
    s.textContent = [
      /* the photograph invites a tap */
      '[id^="rn-bb-"] .bb-main,[id^="rn-hp-reya-"] .rc-media{cursor:zoom-in;touch-action:pan-y}',
      /* the thumbnail rail must scroll sideways under a finger. It is already
         overflow-x:auto, but without an explicit touch-action iOS can hand the
         gesture to the page instead of the strip. */
      '[id^="rn-bb-"] .bb-thumbs,[id^="rn-hp-reya-"] .rc-thumbs{touch-action:pan-x;-webkit-overflow-scrolling:touch;overscroll-behavior-x:contain}',
      '[id^="rn-bb-"] .bb-th,[id^="rn-hp-reya-"] .rc-th{cursor:pointer}',
      /* ---- the zoom overlay ---- */
      '.rn-zoom{position:fixed;inset:0;z-index:2147483000;background:rgba(17,17,17,.94);display:flex;align-items:center;justify-content:center;opacity:0;transition:opacity .18s ease;touch-action:pan-y}',
      '.rn-zoom[hidden]{display:none}',
      '.rn-zoom.on{opacity:1}',
      '.rn-zoom img,.rn-zoom video{max-width:100%;max-height:100%;width:auto;height:auto;object-fit:contain;display:block;user-select:none;-webkit-user-drag:none}',
      '.rn-zoom img[hidden],.rn-zoom video[hidden]{display:none}',
      '.rn-zoom-x{position:absolute;top:max(12px,env(safe-area-inset-top));right:12px;width:44px;height:44px;border:0;border-radius:50%;background:rgba(255,255,255,.14);color:#fff;font-size:26px;line-height:1;cursor:pointer}',
      '.rn-zoom-n{position:absolute;bottom:max(16px,env(safe-area-inset-bottom));left:0;right:0;text-align:center;color:#fff;font-size:13px;letter-spacing:.04em;opacity:.75;pointer-events:none}',
      /* Arrows are for a mouse. On a phone the gesture is the interface, and a
         44px target is the accessible minimum for anyone not swiping. */
      '.rn-zoom-a{position:absolute;top:50%;transform:translateY(-50%);width:44px;height:44px;border:0;border-radius:50%;background:rgba(255,255,255,.14);color:#fff;font-size:24px;line-height:1;cursor:pointer;z-index:1}',
      '.rn-zoom-a.p{left:10px}.rn-zoom-a.n{right:10px}',
      '@media (prefers-reduced-motion:reduce){.rn-zoom{transition:none}}'
    ].join('\n');
    document.head.appendChild(s);
  }

  var box = null;

  function overlay() {
    if (box) return box;
    box = document.createElement('div');
    box.className = 'rn-zoom';
    box.setAttribute('hidden', '');
    box.setAttribute('role', 'dialog');
    box.setAttribute('aria-modal', 'true');
    box.setAttribute('aria-label', 'Product photograph');
    box.innerHTML =
      '<button type="button" class="rn-zoom-a p" aria-label="Previous photograph">‹</button>' +
      '<img alt="">' +
      '<video muted loop playsinline controls preload="none" hidden></video>' +
      '<button type="button" class="rn-zoom-a n" aria-label="Next photograph">›</button>' +
      '<button type="button" class="rn-zoom-x" aria-label="Close">×</button>' +
      '<p class="rn-zoom-n" data-zoom-note></p>';
    document.body.appendChild(box);
    return box;
  }

  /* The section the overlay is currently showing, so its arrows and swipes go
     back to the right gallery on a page carrying more than one. */
  var liveRoot = null, liveCfg = null;

  function shown(el) {
    if (!el || el.hidden) return false;
    var cs = window.getComputedStyle ? window.getComputedStyle(el) : null;
    return !(cs && (cs.display === 'none' || cs.visibility === 'hidden'));
  }

  /* What the main frame is showing right now: a visible <video> wins (the
     gallery swaps the photograph for one), otherwise the photograph. */
  function mainMedia(root) {
    if (!root || !liveCfg) return null;
    var m = root.querySelector(liveCfg.main);
    if (!m) return null;
    var vids = m.querySelectorAll('video');
    for (var i = 0; i < vids.length; i++) { if (shown(vids[i]) && (vids[i].currentSrc || vids[i].getAttribute('src'))) return vids[i]; }
    return m.querySelector('img');
  }

  function stopVideo(v) {
    if (!v) return;
    try { v.pause(); } catch (e) {}
    if (v.getAttribute('src')) { v.removeAttribute('src'); try { v.load(); } catch (e) {} }
  }

  function paint() {
    var el = mainMedia(liveRoot), o = overlay();
    if (!el) return;
    var big = o.querySelector('img'), bigV = o.querySelector('video');
    if (el.tagName === 'VIDEO') {
      var src = el.currentSrc || el.getAttribute('src');
      big.setAttribute('hidden', '');
      bigV.removeAttribute('hidden');
      if (bigV.getAttribute('src') !== src) {
        var poster = el.getAttribute('poster');
        if (poster) bigV.setAttribute('poster', poster); else bigV.removeAttribute('poster');
        bigV.setAttribute('src', src);
        var t = el.currentTime || 0;
        bigV.addEventListener('loadedmetadata', function once() {
          bigV.removeEventListener('loadedmetadata', once);
          try { if (t > 0 && t < bigV.duration) bigV.currentTime = t; } catch (e) {}
        });
        try { bigV.load(); } catch (e) {}
      }
      var p = bigV.play(); if (p && p['catch']) p['catch'](function () {});
    } else {
      stopVideo(bigV);
      bigV.setAttribute('hidden', '');
      big.removeAttribute('hidden');
      big.setAttribute('src', el.getAttribute('src'));
      big.setAttribute('alt', el.getAttribute('alt') || '');
    }
    var note = o.querySelector('[data-zoom-note]');
    var dots = liveRoot.querySelectorAll(liveCfg.dots);
    if (note && dots.length > 1) {
      var at = 0;
      dots.forEach(function (d, j) { if (d.classList.contains('on')) at = j; });
      note.textContent = (at + 1) + ' of ' + dots.length;
    } else if (note) { note.textContent = ''; }
  }

  function step(dir) {
    if (!liveRoot) return;
    var b = liveRoot.querySelector(dir > 0 ? liveCfg.next : liveCfg.prev);
    if (!b) return;
    b.click();
    /* the section swaps the src synchronously, but give it a frame in case a
       future version animates the change */
    requestAnimationFrame(paint);
  }

  function open(root, cfg) {
    liveRoot = root; liveCfg = cfg;
    var o = overlay();
    paint();
    o.removeAttribute('hidden');
    requestAnimationFrame(function () { o.classList.add('on'); });
    document.documentElement.style.overflow = 'hidden';
    var x = o.querySelector('.rn-zoom-x');
    if (x) x.focus();
  }

  function close() {
    if (!box) return;
    box.classList.remove('on');
    document.documentElement.style.overflow = '';
    stopVideo(box.querySelector('video'));
    /* wait out the fade so it does not vanish mid-transition */
    setTimeout(function () { if (box) box.setAttribute('hidden', ''); }, 180);
    liveRoot = null; liveCfg = null;
  }

  function isOpen() { return box && !box.hasAttribute('hidden'); }

  /* ---- gestures, shared by the photograph and the overlay ---------------- */
  /* A tap is judged on where the finger ENDED UP, not on whether a touchmove
     happened to exceed a threshold along the way. Testing this caught a real
     hole: a slow 15px drag fired move events that each fell under the old 8px
     guard, so it was read as a tap and opened the zoom when the shopper was
     trying to nudge the page. Final displacement cannot be fooled that way. */
  var TAP_SLOP = 10;

  function wireSwipe(el, onSwipe, onTap) {
    var x0 = 0, y0 = 0, tracking = false;
    el.addEventListener('touchstart', function (e) {
      if (e.touches.length !== 1) { tracking = false; return; }
      tracking = true;
      x0 = e.touches[0].clientX; y0 = e.touches[0].clientY;
    }, { passive: true });
    el.addEventListener('touchend', function (e) {
      if (!tracking) return;
      tracking = false;
      var t = e.changedTouches && e.changedTouches[0];
      if (!t) return;
      var dx = t.clientX - x0, dy = t.clientY - y0;
      if (Math.abs(dx) >= SWIPE_MIN && Math.abs(dx) > Math.abs(dy) * SWIPE_RATIO) {
        onSwipe(dx < 0 ? 1 : -1);
      } else if (Math.abs(dx) < TAP_SLOP && Math.abs(dy) < TAP_SLOP && onTap) {
        onTap(e);
      }
    }, { passive: true });
  }

  function skipTap(target, cfg) {
    var base = 'button, a, [role="button"]';
    return !!(target.closest(cfg.skip ? base + ', ' + cfg.skip : base));
  }

  function wireSection(root, cfg) {
    if (root.hasAttribute('data-rn-touch')) return;
    var main = root.querySelector(cfg.main);
    if (!main) return;
    /* nothing to browse: one photograph and no controls */
    if (!root.querySelector(cfg.next) && !root.querySelector(cfg.prev)) return;
    root.setAttribute('data-rn-touch', '1');

    function busy() {
      if (!cfg.block) return false;
      var b = root.querySelector(cfg.block);
      return !!(b && !b.hasAttribute('hidden'));
    }

    wireSwipe(main,
      function (dir) { if (!busy()) { liveRoot = root; liveCfg = cfg; step(dir); } },
      function (e) { if (!busy() && !skipTap(e.target, cfg)) open(root, cfg); });

    /* a mouse still gets the zoom; it just has arrows rather than a flick */
    main.addEventListener('click', function (e) {
      if (busy() || skipTap(e.target, cfg)) return;
      /* a touch device already handled this through touchend */
      if (e.detail === 0) return;
      if (window.matchMedia && window.matchMedia('(hover: none)').matches) return;
      open(root, cfg);
    });
  }

  function wireAll() {
    GALLERIES.forEach(function (cfg) {
      document.querySelectorAll(cfg.root).forEach(function (r) { wireSection(r, cfg); });
    });
  }

  function init() {
    injectStyles();
    wireAll();

    var o = overlay();
    o.querySelector('.rn-zoom-x').addEventListener('click', close);
    o.querySelector('.rn-zoom-a.p').addEventListener('click', function () { step(-1); });
    o.querySelector('.rn-zoom-a.n').addEventListener('click', function () { step(1); });
    /* tapping the backdrop closes; tapping the photograph does not, so a
       mis-tap while browsing does not throw the shopper out */
    o.addEventListener('click', function (e) {
      if (e.target === o) close();
    });
    wireSwipe(o, function (dir) { step(dir); }, null);
    document.addEventListener('keydown', function (e) {
      if (!isOpen()) return;
      if (e.key === 'Escape') close();
      else if (e.key === 'ArrowRight') step(1);
      else if (e.key === 'ArrowLeft') step(-1);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else { init(); }

  /* Sections re-render in the theme editor; pick up any new buy box. */
  document.addEventListener('shopify:section:load', wireAll);
})();
